import { useState } from "react";
import { MagnifyingGlass, Users, VideoCamera, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ToolShell } from "@/components/ToolShell";
import { CopyButton } from "@/components/CopyButton";
import { useSeo } from "@/lib/seo";

const compact = (n) => (n == null ? "—" : Intl.NumberFormat("en", { notation: "compact" }).format(n));

export default function ChannelFinder() {
  useSeo("YouTube Channel Finder", "Find any YouTube channel's ID, subscriber count and stats from a handle, URL or name.");
  const [query, setQuery] = useState("");
  const [ch, setCh] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await api.get("/channel", { params: { query } });
      setCh(res.data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <ToolShell slug="channel-finder" title="Channel Finder" tagline="Enter a @handle, channel URL or name — get the channel ID and live stats." hasResults={!!ch}>
      <form onSubmit={search} className="surface rounded-[20px] p-6 sm:p-8 glow-ring">
        <label className="text-sm font-semibold block mb-2">Handle, URL or name</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="channel-query-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="@mkbhd"
            className="flex-1 bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] px-4 py-3 text-sm outline-none focus:border-[var(--glow)]"
          />
          <button
            data-testid="channel-search-btn"
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-[14px] bg-[var(--glow)] text-white font-semibold text-sm hover:bg-[#6a4ce6] transition-colors disabled:opacity-60"
          >
            <MagnifyingGlass size={16} weight="bold" /> {loading ? "Searching..." : "Find channel"}
          </button>
        </div>
      </form>

      {ch && (
        <div className="mt-8 surface rounded-[20px] p-6 sm:p-8 fade-up" data-testid="channel-result-card">
          <div className="flex items-center gap-4">
            {ch.avatar && <img src={ch.avatar} alt={ch.title} className="w-16 h-16 rounded-full" />}
            <div className="min-w-0">
              <h2 className="font-bold text-xl truncate" data-testid="channel-title">{ch.title}</h2>
              <p className="text-[var(--muted)] text-sm">{ch.handle}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-6">
            {[
              { icon: Users, label: "Subscribers", value: compact(ch.subscriberCount) },
              { icon: VideoCamera, label: "Videos", value: compact(ch.videoCount) },
              { icon: Eye, label: "Total views", value: compact(ch.viewCount) },
            ].map((s) => (
              <div key={s.label} className="bg-[var(--bg)] border border-[var(--border-c)] rounded-[14px] p-4">
                <s.icon size={18} color="var(--glow)" weight="duotone" />
                <p className="font-display font-black text-lg sm:text-2xl mt-2">{s.value}</p>
                <p className="text-[var(--muted)] text-xs">{s.label}</p>
              </div>
            ))}
          </div>
          {ch.description && <p className="text-[var(--muted)] text-sm mt-5">{ch.description}</p>}
          <div className="flex items-center gap-3 mt-6 flex-wrap">
            <code className="text-xs bg-[var(--bg)] border border-[var(--border-c)] rounded-full px-4 py-2" data-testid="channel-id">{ch.channelId}</code>
            <CopyButton text={ch.channelId} label="Copy channel ID" testId="copy-channel-id-btn" />
          </div>
        </div>
      )}
    </ToolShell>
  );
}
